(() => {
  const initCover = (cover) => {
    if (!cover || cover.dataset.scratchReady === 'true') return;

    const canvas = cover.querySelector('[data-scratch-canvas]');
    const hint = cover.querySelector('[data-scratch-hint]');
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    cover.dataset.scratchReady = 'true';

    const content = cover.closest('[data-scratch-content]') || cover.closest('.hero-slider-inside__content--scratch');
    const threshold = Math.min(90, Math.max(15, Number(cover.dataset.threshold || 45))) / 100;
    const coverSrc = cover.dataset.coverSrc || '';
    const coverColor = cover.dataset.coverColor || '#c8bfb4';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let coverImage = null;
    let drawing = false;
    let revealed = false;
    let lastPoint = null;
    let checkQueued = false;
    let hasSized = false;
    let hasInteracted = false;
    let initialOpaque = 0;

    const countOpaqueSamples = () => {
      const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let opaque = 0;
      const stride = 16;

      for (let index = 3; index < pixels.length; index += 4 * stride) {
        if (pixels[index] >= 24) opaque += 1;
      }

      return opaque;
    };

    const paintCover = (width, height) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const dpr = canvas.width / Math.max(width, 1);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (coverImage) {
        const imageRatio = coverImage.width / Math.max(coverImage.height, 1);
        const boxRatio = width / Math.max(height, 1);
        let drawWidth = width;
        let drawHeight = height;
        let offsetX = 0;
        let offsetY = 0;

        if (imageRatio > boxRatio) {
          drawWidth = width;
          drawHeight = width / imageRatio;
          offsetY = (height - drawHeight) / 2;
        } else {
          drawHeight = height;
          drawWidth = height * imageRatio;
          offsetX = (width - drawWidth) / 2;
        }

        ctx.drawImage(coverImage, offsetX, offsetY, drawWidth, drawHeight);
      } else {
        ctx.fillStyle = coverColor;
        ctx.fillRect(0, 0, width, height);
      }

      ctx.restore();
      try {
        initialOpaque = countOpaqueSamples();
      } catch (error) {
        initialOpaque = 1;
      }
    };

    const sizeCanvas = (forcePaint = false) => {
      if (revealed || drawing) return;
      if (coverSrc && !coverImage && !forcePaint) return;

      cover.style.removeProperty('width');
      cover.style.removeProperty('height');
      canvas.style.removeProperty('width');
      canvas.style.removeProperty('height');

      const width = Math.max(1, Math.round(cover.clientWidth || Number(cover.dataset.scratchWidthMobile) || 300));
      const height = Math.max(1, Math.round(cover.clientHeight || Number(cover.dataset.scratchHeightMobile) || 139));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const canvasWidth = Math.round(width * dpr);
      const canvasHeight = Math.round(height * dpr);

      if (!forcePaint && hasSized && canvas.width === canvasWidth && canvas.height === canvasHeight) return;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      paintCover(width, height);
      hasSized = true;
    };

    const pointFromEvent = (event) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const brushRadius = () => {
      const rect = canvas.getBoundingClientRect();
      return Math.max(22, Math.min(rect.width, rect.height) * 0.1);
    };

    const scratchTo = (point) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / Math.max(rect.width, 1);
      const scaleY = canvas.height / Math.max(rect.height, 1);

      ctx.save();
      ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000000';
      ctx.strokeStyle = '#000000';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineWidth = brushRadius() * 2;

      if (lastPoint) {
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(point.x, point.y, brushRadius(), 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      lastPoint = point;
    };

    const hideCover = () => {
      cover.classList.add('is-gone');
      cover.style.display = 'none';
    };

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      drawing = false;
      lastPoint = null;
      cover.classList.add('is-revealed');
      cover.setAttribute('aria-hidden', 'true');
      cover.tabIndex = -1;
      content?.classList.add('is-scratch-revealed');
      cover.dispatchEvent(new CustomEvent('scratch:revealed', { bubbles: true }));

      if (reducedMotion) {
        hideCover();
        return;
      }

      const onFadeEnd = (event) => {
        if (event.propertyName !== 'opacity') return;
        cover.removeEventListener('transitionend', onFadeEnd);
        hideCover();
      };

      cover.addEventListener('transitionend', onFadeEnd);
      window.setTimeout(hideCover, 550);
    };

    const queuedRevealCheck = () => {
      if (revealed || checkQueued) return;
      checkQueued = true;

      requestAnimationFrame(() => {
        checkQueued = false;
        if (revealed) return;

        if (initialOpaque <= 0) return;

        const remainingOpaque = countOpaqueSamples();
        if ((initialOpaque - remainingOpaque) / initialOpaque >= threshold) reveal();
      });
    };

    const onPointerDown = (event) => {
      if (revealed || event.button === 1 || event.button === 2) return;
      event.preventDefault();
      event.stopPropagation();
      if (!hasSized) sizeCanvas();
      hasInteracted = true;
      drawing = true;
      lastPoint = null;
      cover.classList.add('is-scratching');
      hint?.classList.add('is-hidden');
      cover.setPointerCapture(event.pointerId);
      scratchTo(pointFromEvent(event));
      queuedRevealCheck();
    };

    const onPointerMove = (event) => {
      if (!drawing || revealed) return;
      event.preventDefault();
      event.stopPropagation();
      scratchTo(pointFromEvent(event));
      queuedRevealCheck();
    };

    const onPointerUp = (event) => {
      if (!drawing) return;
      drawing = false;
      lastPoint = null;
      cover.classList.remove('is-scratching');
      if (cover.hasPointerCapture(event.pointerId)) {
        cover.releasePointerCapture(event.pointerId);
      }
      queuedRevealCheck();
    };

    const onKeyDown = (event) => {
      if (revealed) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        reveal();
      }
    };

    cover.addEventListener('scratch:resize', () => {
      if (!revealed && !drawing) sizeCanvas(true);
    });

    cover.addEventListener('pointerdown', onPointerDown);
    cover.addEventListener('pointermove', onPointerMove);
    cover.addEventListener('pointerup', onPointerUp);
    cover.addEventListener('pointercancel', onPointerUp);
    cover.addEventListener('keydown', onKeyDown);

    const mediaQuery = window.matchMedia('(min-width: 990px)');
    const onBreakpointChange = () => {
      if (!revealed && !drawing && !hasInteracted) sizeCanvas();
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onBreakpointChange);
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!revealed && !drawing && !hasInteracted) sizeCanvas();
    });
    resizeObserver.observe(cover);

    if (reducedMotion) {
      reveal();
      return;
    }

    if (coverSrc) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        coverImage = image;
        sizeCanvas(true);
      };
      image.onerror = () => sizeCanvas(true);
      image.src = coverSrc;
    } else {
      sizeCanvas();
    }
  };

  const init = (scope = document) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-scratch-cover]').forEach(initCover);
  };

  window.HeroSliderInsideScratch = { init };

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  init(document);
})();
